import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type VerificationFrame, type VerificationStatus } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { useT } from '@/i18n/useT';
import { tenantKeys } from '@/tenancy/hooks';

/**
 * Feature A — the customer completes Pocket Option's robot check themselves.
 *
 * What the customer sees is a live picture of their OWN bot's sign-in page.
 * They click the checkbox; the click is relayed to that browser. The moment the
 * browser reaches the funded cabinet the server stops sending frames and closes
 * the session, so the account itself is never shown here.
 *
 * Polling, not a socket: a reCAPTCHA checkbox does not need 30 fps, and this
 * way the panel inherits every auth and error rule the rest of the API has.
 *
 * The same component serves the Admin Panel — `base` is the only difference.
 */
const FRAME_MS = 500;

export function VerificationPanel({ base, onClose }: { base?: string; onClose?: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [frame, setFrame] = useState<VerificationFrame | null>(null);
  const [error, setError] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);
  const stopped = useRef(false);
  // Held in a ref as well as state: the poll loop closes over the first render,
  // and a finished session must stop it even if a re-render has not happened.
  const finished = useRef(false);

  const finish = useCallback(
    async (tell: boolean) => {
      if (stopped.current) return;
      stopped.current = true;
      finished.current = true;
      if (tell) await api.verifyFinish(base).catch(() => undefined);
      // The Connect screen decides what to show from the link status, so it has
      // to re-read it once this is over.
      void qc.invalidateQueries({ queryKey: tenantKeys.link });
      void qc.invalidateQueries({ queryKey: tenantKeys.status });
    },
    [base, qc]
  );

  // Start once on mount; close on unmount so a closed tab cannot leave the
  // tenant's profile locked and its bot stopped.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const s = await api.verifyStart(base);
        if (!alive) return;
        setStatus(s);
        if (s.phase === 'closed') {
          finished.current = true;
          void qc.invalidateQueries({ queryKey: tenantKeys.link });
        }
      } catch (e) {
        if (alive) setError(errorMessage(e));
        finished.current = true;
      }
    })();
    // A CLOSED TAB must release the session too, not just a click on Cancel.
    // While a session is open the customer's bot is STOPPED and its Chromium
    // profile is locked, so waiting out the 10 minute server TTL means ten
    // minutes of a stopped bot because somebody closed a tab. An ordinary
    // fetch does not survive the page going away; sendBeacon is queued by the
    // browser and delivered after it does.
    const release = () => {
      if (stopped.current) return;
      stopped.current = true;
      const url = `${base ?? '/api/tenant/verify'}/finish`;
      let sent = false;
      try {
        sent = navigator.sendBeacon?.(url, new Blob([], { type: 'application/json' })) ?? false;
      } catch {
        sent = false;
      }
      if (!sent) void api.verifyFinish(base).catch(() => undefined);
    };
    window.addEventListener('pagehide', release);

    return () => {
      alive = false;
      window.removeEventListener('pagehide', release);
      release();
      void qc.invalidateQueries({ queryKey: tenantKeys.link });
    };
  }, [base, qc]);

  // Frame loop. setTimeout rather than setInterval so a slow frame cannot
  // stack requests on a browser that is already busy.
  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    const tick = async () => {
      if (!alive || finished.current) return;
      try {
        const f = await api.verifyFrame(base);
        if (!alive) return;
        setFrame(f);
        setStatus(f);
        if (f.phase === 'closed' || f.phase === 'verified') {
          finished.current = true;
          void finish(false);
          return;
        }
      } catch (e) {
        // 409 is normal and expected: the session has left the sign-in page
        // (usually because it succeeded). Read the status rather than treating
        // it as a failure.
        if (e instanceof ApiError && e.status === 409) {
          const s = await api.verifyStatus(base).catch(() => null);
          if (!alive) return;
          if (s) {
            setStatus(s);
            if (s.phase === 'closed' || s.phase === 'verified') {
              finished.current = true;
              void finish(false);
              return;
            }
          }
        } else if (alive) {
          setError(errorMessage(e));
          finished.current = true;
          return;
        }
      }
      if (alive && !finished.current) timer = window.setTimeout(tick, FRAME_MS);
    };
    timer = window.setTimeout(tick, FRAME_MS);
    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [base, finish]);

  /**
   * Relay a click in the coordinates of the image AS RENDERED.
   *
   * The server maps it back to the browser's viewport. Sending the rendered
   * size with every click means the mapping never depends on the panel and the
   * browser agreeing about a size — which is exactly the kind of assumption
   * that ends with the customer clicking and nothing happening.
   */
  const onImageClick = async (e: React.MouseEvent<HTMLImageElement>) => {
    const img = imgRef.current;
    if (!img || finished.current) return;
    const r = img.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    try {
      const s = await api.verifyClick(
        { x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height },
        base
      );
      setStatus(s);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const phase = status?.phase ?? 'starting';
  const done = phase === 'verified' || phase === 'closed';
  const secondsLeft = status ? Math.max(0, Math.round(status.msRemaining / 1000)) : 0;

  return (
    <section className="bot-card verify-panel">
      <div className="bot-card-head">
        <h2>{t('bot.verifyTitle')}</h2>
        {!done && status ? (
          <span className="muted">{t('bot.verifyTimeLeft', { m: Math.floor(secondsLeft / 60), s: String(secondsLeft % 60).padStart(2, '0') })}</span>
        ) : null}
      </div>

      {error ? <div className="bot-alert error">{error}</div> : null}

      {done ? (
        <div className={`bot-alert ${status?.outcome === 'verified' ? 'ok' : 'warn'}`}>
          {status?.outcome === 'verified'
            ? t('bot.verifyVerified')
            : status?.outcome === 'timeout'
              ? t('bot.verifyTimedOut')
              : status?.detail || t('bot.verifyClosed')}
        </div>
      ) : (
        <>
          <p className="muted">{t('bot.verifyClickPrompt')}</p>
          {frame ? (
            <img
              ref={imgRef}
              className="verify-frame"
              src={`data:image/jpeg;base64,${frame.jpegBase64}`}
              width={frame.width}
              height={frame.height}
              alt={t('bot.verifyTitle')}
              onClick={onImageClick}
              draggable={false}
            />
          ) : (
            <p className="muted">{t('bot.verifyOpening')}</p>
          )}
        </>
      )}

      <button
        type="button"
        className="bot-btn ghost"
        onClick={() => {
          void finish(true);
          onClose?.();
        }}
      >
        {done ? t('common.close') : t('common.cancel')}
      </button>
    </section>
  );
}
